import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, FormControl, InputLabel, Select, MenuItem, CircularProgress, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SeanceForm from './SeanceForm';
import api from '../../api';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function AddSeance() {
  const [classes, setClasses] = useState([]);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [eleves, setEleves] = useState([]);
  const [selectedEleve, setSelectedEleve] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const seanceTokenQuery = searchParams.get('seanceToken');
  const eleveQuery = searchParams.get('eleve');

  // eleveInfo contains student object when we know who is connected (via token or via eleve=ID)
  const [eleveInfo, setEleveInfo] = useState(null);
  const [loadingEleveInfo, setLoadingEleveInfo] = useState(false);

  useEffect(() => {
    // Si on est en mode seanceToken (élève via QR), on NE CHARGE PAS les classes (route protégée)
    if (seanceTokenQuery) {
      setClasses([]);
      return;
    }

    // Charger les classes du prof connecté (utile si on affiche les selects)
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/classes');
        if (!mounted) return;
        setClasses(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        // On ignore les 401/403 ici : si l'utilisateur n'a pas droit, on laisse classes vide
        console.warn('Impossible de charger /classes (peut être non autorisé) :', err?.response?.status || err);
        setClasses([]);
      }
    })();
    return () => { mounted = false; };
  }, [seanceTokenQuery]);

  useEffect(() => {
    // Si eleveQuery (venant de MonCarnet), pré-sélectionner cet élève et récupérer ses infos
    // Si seanceTokenQuery présent, préférer la récupération via token-info (permet affichage nom/prenom)
    const fetchByTokenOrId = async () => {
      setEleveInfo(null);
      setSelectedEleve(eleveQuery || '');
      if (!seanceTokenQuery && !eleveQuery) return;

      setLoadingEleveInfo(true);
      try {
        if (seanceTokenQuery) {
          // token-info renvoie { eleve }
          const res = await api.get(`/muscu/token-info?seanceToken=${encodeURIComponent(seanceTokenQuery)}`);
          if (res.data && res.data.eleve) {
            setEleveInfo(res.data.eleve);
            setSelectedEleve(res.data.eleve._id);
          } else {
            setEleveInfo(null);
          }
        } else if (eleveQuery) {
          // récupérer via /eleves/:id
          const res = await api.get(`/eleves/${eleveQuery}`);
          if (res.data) {
            setEleveInfo(res.data);
            // standard fields
            setSelectedEleve(res.data._id || res.data.id);
          } else {
            setEleveInfo(null);
          }
        }
      } catch (err) {
        console.error('Erreur récupération élève AddSeance', err);
        setEleveInfo(null);
      } finally {
        setLoadingEleveInfo(false);
      }
    };

    fetchByTokenOrId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seanceTokenQuery, eleveQuery]);

  useEffect(() => {
    // Charger les élèves quand une classe est sélectionnée (uniquement utile si on affiche les selects)
    // NE PAS charger les élèves si on est en mode seanceToken (élève identifié par token)
    if (!selectedClasse || seanceTokenQuery) {
      setEleves([]);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        const res = await api.get(`/eleves?classe=${selectedClasse}`);
        if (!mounted) return;
        setEleves(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.warn('Impossible de charger /eleves (peut être non autorisé) :', err?.response?.status || err);
        setEleves([]);
      }
    })();
    return () => { mounted = false; };
  }, [selectedClasse, seanceTokenQuery]);

  const handleSaveSeance = async (seancePayload) => {
    // Construire le payload selon le contexte
    const payload = { ...seancePayload };

    // Si on a un seance token, on laisse le backend utiliser le token pour identifier l'élève.
    // Si on a eleveInfo (arrivé via eleve param) ou selectedEleve (sélection manuelle), on inclut eleve et éventuellement classe.
    const config = {};
    if (seanceTokenQuery) {
      config.headers = { 'x-seance-token': seanceTokenQuery };
    } else {
      // include eleve id if present (from eleveInfo or selectedEleve)
      const eleveIdToSend = eleveInfo ? (eleveInfo._id || eleveInfo.id) : selectedEleve;
      if (eleveIdToSend) payload.eleve = eleveIdToSend;
      if (selectedClasse) payload.classe = selectedClasse;
    }

    const res = await api.post('/muscu/seances', payload, config);
    return res.data;
  };

  const goToMonCarnet = () => {
    // Navigate back to MonCarnet with the eleve id or token preserved if available
    const params = new URLSearchParams();
    if (eleveInfo && (eleveInfo._id || eleveInfo.id)) params.set('eleve', eleveInfo._id || eleveInfo.id);
    else if (selectedEleve) params.set('eleve', selectedEleve);
    if (seanceTokenQuery) params.set('seanceToken', seanceTokenQuery);
    const q = params.toString();
    navigate(`/carnet-muscu/mon-carnet${q ? '?' + q : ''}`);
  };

  // helper renderers for selects to avoid label overlap and display full names
  const renderClasseValue = (val) => {
    if (!val) return 'Aucune';
    const found = classes.find(c => c._id === val);
    return found ? found.nom : val;
  };

  const renderEleveValue = (val) => {
    if (!val) return 'Tous / Aucun';
    const found = eleves.find(e => e._id === val);
    if (found) return `${found.prenom} ${found.nom}`;
    return val;
  };

  return (
    <Paper sx={{ p: 3, mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton aria-label="retour" onClick={() => navigate(-1)} size="small">
          <ArrowBackIcon />
        </IconButton>
      </Box>

      {/* If loading eleve info show spinner */}
      {loadingEleveInfo ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress /></Box>
      ) : eleveInfo ? (
        // If we have student info (either from token or eleve id): display centered title + name
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>Ma séance du jour</Typography>
          <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center', mt: 1 }}>
            {eleveInfo.prenom} {eleveInfo.nom}
          </Typography>
        </Box>
      ) : (
        // No known eleve: show the selects (classe + eleve)
        <Box sx={{ display: 'grid', gap: 2, mb: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="classe-select" shrink={!!selectedClasse}>Classe</InputLabel>
            <Select
              labelId="classe-select"
              value={selectedClasse}
              label="Classe"
              onChange={e => setSelectedClasse(e.target.value)}
              renderValue={renderClasseValue}
            >
              <MenuItem value="">Aucune</MenuItem>
              {classes.map(c => <MenuItem key={c._id} value={c._id}>{c.nom}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="eleve-select" shrink={!!selectedEleve}>Élève</InputLabel>
            <Select
              labelId="eleve-select"
              value={selectedEleve}
              label="Élève"
              onChange={e => setSelectedEleve(e.target.value)}
              renderValue={renderEleveValue}
            >
              <MenuItem value="">Tous / Aucun</MenuItem>
              {eleves.map(e => <MenuItem key={e._id} value={e._id}>{e.prenom} {e.nom}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Pass seanceToken into SeanceForm so it can use it for /muscu/settings, and keep goToMonCarnet */}
      <SeanceForm seanceToken={seanceTokenQuery} onSave={handleSaveSeance} onOpenMonCarnet={goToMonCarnet} />
    </Paper>
  );
}